import type { Ref } from "vue"
import type { Router } from "vue-router"
import type { Source } from "@types"
import type { ToastMessageOptions } from "primevue"
import { restrictedAccessRedirectToast } from "@core/router/restrictedAccessNavigation"

type ToastLike = {
    add(message: ToastMessageOptions): void
}

/**
 * Retire la source du cache Pinia pour éviter un affichage fantôme après redirection.
 */
export function removeSourceFromSourcesList(
    sources: Ref<Source[]>,
    _id_source: string
): void {
    sources.value = sources.value.filter((s) => s._id_source !== _id_source)
}

/**
 * Redirige hors de la fiche source vers l'accueil.
 *
 * DEV-1846 : jamais de `router.back()`. Un retour arrière rend la page precedente de
 * l'HISTORIQUE du navigateur, pas une destination de l'application : depuis un lien
 * externe ou un rechargement il renvoyait hors de l'app, et depuis une autre source il
 * pouvait reboucler. On prend le meme chemin que l'erreur de chargement voisine
 * (`PageSourceWrapper.vue`) : `push({ name: "Home" })`.
 */
export async function navigateAwayFromSourceShell(
    router: Router
): Promise<void> {
    await router.push({ name: "Home", query: {} })
}

/**
 * Toast groupe notifications : lien CompanySource `views` révoqué (plus d’accès à la source).
 */
export function addSourceViewsLinkRevokedToast(toast: ToastLike): void {
    toast.add({
        group: "notifications",
        severity: "warn",
        summary: restrictedAccessRedirectToast.sourceViewsLinkRevoked.summary,
        detail: restrictedAccessRedirectToast.sourceViewsLinkRevoked.detail,
        life: 6000,
    })
}

/**
 * Retrait du cache, navigation arrière ou accueil, notification d’accès retiré.
 * @param navigationBlockRef - Optionnel : mis à `true` pendant la navigation (ex. masquer le router-view enfant).
 */
export async function redirectOnSourceViewsLinkRevoked(params: {
    router: Router
    toast: ToastLike
    sources: Ref<Source[]>
    _id_source: string
    navigationBlockRef?: Ref<boolean>
}): Promise<void> {
    const { router, toast, sources, _id_source, navigationBlockRef } = params
    if (navigationBlockRef) {
        navigationBlockRef.value = true
    }
    try {
        removeSourceFromSourcesList(sources, _id_source)
        await navigateAwayFromSourceShell(router)
        addSourceViewsLinkRevokedToast(toast)
    } finally {
        if (navigationBlockRef) {
            navigationBlockRef.value = false
        }
    }
}
